import { useQuery } from "@tanstack/react-query";
import type { PublicProperty } from "@shared/schema";
import { PropertyListings } from "@/components/property/property-listings";
import { Link } from "wouter";
import { useBookCta } from "@/hooks/use-book-href";
import { KUALOA_STAY_PATH } from "@shared/property-context";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { StickyMobileBookCta } from "@/components/layout/sticky-mobile-book-cta";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { FaqSection } from "@/components/seo/faq-section";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { PageHero } from "@/components/layout/page-hero";
import { SectionHeading } from "@/components/ui/section-heading";
import { useSEO, generateTouristTripSchema, generateTravelActionSchema, generateBreadcrumbSchema, generateFAQSchema, LOCATION_KEYWORDS } from "@/lib/seo-utils";
import { wavesVideo, parallaxBg, wavesPoster, portfolioHeroVideo, portfolioHeroPoster } from "@/lib/media-assets";
import { LazyVideo } from "@/components/ui/lazy-video";
import { ViatorWidget } from "@/components/property/viator-widget";

const experienceFaqs = [
  {
    question: "What activities are near the Ka'a'awa vacation rental?",
    answer: "Kualoa Ranch is across the street. Popular day trips include North Shore surf towns, Polynesian Cultural Center, Hanauma Bay snorkeling, and windward coast hikes, all within easy driving distance.",
  },
  {
    question: "Do I book tours through the vacation rental site?",
    answer: "Tours on this page are booked securely through our Viator partner widget. Your beach house stay is booked separately on our booking page for the best direct rate.",
  },
  {
    question: "What is the best home base for Oahu adventures?",
    answer: "Ka'a'awa on the windward coast offers a quieter alternative to Waikiki with oceanfront lodging and quick access to Kualoa Ranch, Mokoli'i (Chinaman's Hat), and scenic coastal drives.",
  },
];

export default function ExperiencesPage() {
  const {data: homes = []} = useQuery<PublicProperty[]>({queryKey: ["/api/properties"]});
  const seoHead = useSEO({
    pageType: "experiences",
    keywords: `Oahu experiences, Hawaiian activities, ${LOCATION_KEYWORDS.activities}, snorkeling oahu, hiking oahu, kualoa ranch tours, polynesian cultural center, north shore surfing, oahu adventure tours, things to do oahu, oahu excursions`,
    ogType: "website",
    canonical: "https://vacationrentaloahu.co/experiences",
    twitterCard: "summary_large_image",
    structuredData: [
      generateTouristTripSchema(),
      generateTravelActionSchema(),
      generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Experiences", url: "/experiences" }
      ]),
      generateFAQSchema(experienceFaqs),
    ]
  });

  return (
    <>
    {seoHead}
    <div className="min-h-screen bg-shell pb-24 lg:pb-0 selection:bg-sun selection:text-shell">
      <Header />
      <main id="main-content">
        <PageHero
          title="Hawaiian Experiences"
          subtitle="Book island adventures from our Ka'a'awa home base: Kualoa Ranch tours, snorkeling, cultural experiences, and North Shore day trips."
          videoSrc={wavesVideo}
          posterSrc={wavesPoster}
          imageAlt="Breathtaking Oahu sunset beach scene with gentle waves and tropical coastline - discover authentic Hawaiian experiences and adventures"
        />



        <section aria-label="Tours and activities" className="py-6 md:py-8 bg-shell border-y border-sand-deep">
          <div className="container mx-auto px-6">


            <ScrollReveal className="max-w-6xl mx-auto">
              <div className="bg-shell p-2 md:p-4 rounded-2xl border border-sand-deep shadow-sm">
                <ViatorWidget
                  partnerId="P00269401"
                  widgetRef="W-a3edc128-6a3f-4f68-9491-52bf79666fa5"
                  minHeight="600px"
                  className="rounded-xl overflow-hidden"
                />
              </div>
            </ScrollReveal>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-shell">
          <div className="container mx-auto px-6 max-w-6xl text-center">
            <Breadcrumbs items={[{ name: "Home", url: "/" }, { name: "Experiences", url: "/experiences" }]} className="mb-8 justify-center" />
            <p className="text-luxury-lg text-ink-soft leading-relaxed mb-10">
              Stay at our <Link href={KUALOA_STAY_PATH} className="text-ink border-b border-sand-deep hover:border-sun transition-colors duration-300">beachfront vacation rental in Ka'a'awa</Link>, then explore Oahu through
              the activities above. For DIY planning, browse our{" "}
              <Link href="/blog/category/snorkeling-water-activities" className="text-ink border-b border-sand-deep hover:border-sun transition-colors duration-300">snorkeling guides</Link>,{" "}
              <Link href="/blog/category/hiking-outdoor-adventure" className="text-ink border-b border-sand-deep hover:border-sun transition-colors duration-300">hiking trails</Link>, and{" "}
              <Link href="/blog/category/trip-planning-itineraries" className="text-ink border-b border-sand-deep hover:border-sun transition-colors duration-300">itineraries</Link>.
            </p>
            <PropertyListings properties={homes} />
          </div>
        </section>

        <section
          className="scenic-editorial relative overflow-hidden"
          aria-labelledby="why-book-heading"
        >
          <div className="absolute inset-0 z-0" aria-hidden="true">
            {/* The coast clip, the same footage as the homepage hero: no single
                home in frame, since the promise applies to every home. */}
            <LazyVideo
              src={portfolioHeroVideo}
              poster={portfolioHeroPoster}
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 h-full w-full object-cover"
              aria-label="The windward coast beneath the Ko'olau range, from the air"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, transparent 65%, rgba(20,20,16,.15) 100%)",
              }}
            />
          </div>

          <div className="relative z-10 container mx-auto px-5 md:px-8 max-w-[1040px] py-16 sm:py-20 md:py-28">
            <ScrollReveal>
              <div className="media-copy-panel p-6 text-center max-w-2xl mx-auto mb-10 sm:mb-12 md:mb-16">
                <p className="wordmark-gold text-[11px] md:text-xs font-sans font-normal uppercase tracking-[0.3em] mb-3">
                  Our Promise
                </p>
                <h2
                  id="why-book-heading"
                  className="wordmark-gold font-serif font-[500] text-[clamp(28px,5.2vw,44px)] leading-[1.15] tracking-tight"
                >
                  Why Book With Us
                </h2>
                <div className="flex items-center gap-3 mt-[18px] w-full max-w-[min(80vw,360px)] mx-auto">
                  <span className="h-px flex-1 bg-[#E3CF98]/55" />
                  <span className="w-[7px] h-[7px] rotate-45 bg-[#E3CF98]" />
                  <span className="h-px flex-1 bg-[#E3CF98]/55" />
                </div>
                <p className="mt-5 wordmark-gold text-[21px] leading-relaxed font-serif italic max-w-xl mx-auto">
                  Book tours from your Ka&apos;a&apos;awa home base with secure checkout, flexible options, and trusted local operators.
                </p>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              {[
                {
                  title: "Secure Booking",
                  body: "All bookings are protected with secure payment processing and a customer guarantee.",
                  icon: (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  ),
                },
                {
                  title: "Flexible Booking",
                  body: "Many experiences offer free cancellation and flexible scheduling when plans change.",
                  icon: (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  ),
                },
                {
                  title: "Top Rated Tours",
                  body: "Choose from verified reviews and top-rated local tour operators across Oahu.",
                  icon: (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  ),
                },
              ].map((item, index) => (
                <ScrollReveal key={item.title} delay={index * 0.08}>
                  <article className="h-full text-center md:text-left px-5 py-6 sm:px-6 sm:py-7 media-copy-panel border border-white/25 transition-colors duration-300">
                    <svg className="h-7 w-7 text-[#E3CF98] mx-auto md:mx-0 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      {item.icon}
                    </svg>
                    <h3 className="text-luxury-xl font-serif font-[500] wordmark-gold mb-2.5 tracking-luxury-tight">
                      {item.title}
                    </h3>
                    <p className="text-[18px] wordmark-gold leading-relaxed font-serif">
                      {item.body}
                    </p>
                  </article>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28 bg-shell border-t border-sand-deep">
          <div className="container mx-auto px-6 max-w-3xl">
            <FaqSection title="Experience booking FAQ" items={experienceFaqs} />
          </div>
        </section>
      </main>
      <StickyMobileBookCta />
      <Footer />
    </div>
    </>
  );
}
